import { useApolloClient } from '@apollo/client';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useState } from 'react';

import {
  ClickReasonsEnum,
  type ContentTopic,
  type EntityPageContractType,
  type EntityPageFocusType,
  type EntityPagePublicationStatus,
  type EntityPageVisibility,
} from '@xing-com/crate-common-graphql-types';
import { Redirect } from '@xing-com/crate-core-router';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';

import { EntityPageDocument } from '../../graphql/queries/entity-page-query.gql-types';
import { useEntityPage } from '../../hooks/use-entity-page/use-entity-page';
import { HeaderModule } from '../../types/header-module';
import { EntityPageSocialProof } from '../../types/social-proof';

export type CoverMediaContextType = {
  cover?: string;
};

export type PageContextType = {
  globalId: string;
  pageSlug: string;
  pageId: string;
  focusType: EntityPageFocusType;
  isCompanyPage: boolean;
  isPremiumPage: boolean;
  isGroupPage: boolean;
  isIndustryPage: boolean;
  isInsiderPage: boolean;
  isPublisherPage: boolean;
  isTopicPage: boolean;
  pageSlogan: string;
  isFollowingPage?: boolean | null;
  companyId?: string | null;
  cppId?: string | null;
  socialProof?: EntityPageSocialProof;
  basePath: string;
  contractType: EntityPageContractType;
  publicationStatus: EntityPagePublicationStatus;
  isPageDraft: boolean;
  isPagePublished: boolean;
  visibility?: EntityPageVisibility | null;
  url?: string;
  pageTitle: string;
  upsellActive?: boolean;
  logo?: string;
  isShowingCompanyBanner?: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  contentTopicCollection?: ContentTopic[];
  insiderPageName?: string;
  contentPageGlobalId?: string;
  headerModule?: HeaderModule;
  goBackUrl: () => void;
};

export type PageContextValues = {
  pageContext: PageContextType;
  isSubpage?: boolean;
  showCompanyBanner: () => void;
  setPageLogo: (logo: string) => void;
  toggleFollowPage: (follow: boolean) => void;
};

export const PageContext = createContext<PageContextValues | undefined>(
  undefined
);

type PageContextProviderProps = {
  pageSlug: string;
  isSubpage?: boolean;
};
export const PageContextProvider: FC<
  PropsWithChildren<PageContextProviderProps>
> = ({ pageSlug, isSubpage = false, children }) => {
  const apolloClient = useApolloClient();

  const [isShowingCompanyBanner, setIsShowingCompanyBanner] = useState(false);

  const { pageContext, errorCode, redirectTo } = useEntityPage({
    pageSlug,
  });

  const setPageLogo = (logo: string): void => {
    const variables = {
      id: pageSlug,
      socialProofClickReasonsKey:
        ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    };
    const response = apolloClient.readQuery({
      query: EntityPageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;

    if (!entityPage || entityPage.__typename !== 'EntityPage') {
      return;
    }

    apolloClient.writeQuery({
      query: EntityPageDocument,
      variables,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          logoImage: [
            {
              __typename: 'ScaledImage',
              url: logo,
              reference: 'medium',
            },
          ],
        },
      },
    });
  };
  const toggleFollowPage = (follow: boolean): void => {
    const variables = {
      id: pageSlug,
      socialProofClickReasonsKey:
        ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    };
    const response = apolloClient.readQuery({
      query: EntityPageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;
    const headerModule = response?.headerModule;

    if (
      !entityPage ||
      entityPage.__typename !== 'EntityPage' ||
      !entityPage.userPageContext ||
      !headerModule ||
      headerModule.__typename !== 'EntityPageHeaderModule' ||
      !headerModule.content ||
      headerModule.content.__typename !== 'EntityPageCompanyHeaderContent' ||
      !headerModule.content.followers
    ) {
      return;
    }

    apolloClient.writeQuery({
      query: EntityPageDocument,
      variables,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          userPageContext: {
            ...entityPage.userPageContext,
            userInteractions: {
              __typename: 'EntityPageUserInteractionFollow',
              followState: {
                __typename: 'FollowContext',
                isFollowing: follow,
              },
            },
          },
        },
        headerModule: {
          ...headerModule,
          content: {
            ...headerModule.content,
            followers: {
              ...headerModule.content.followers,
              total: headerModule.content.followers.total + (follow ? 1 : -1),
            },
          },
        },
      },
    });
  };

  if (redirectTo) return <Redirect to={redirectTo} />;
  if (errorCode || !pageContext) return <PageError type={errorCode} />;

  return (
    <PageContext.Provider
      value={{
        pageContext: {
          ...pageContext,
          isShowingCompanyBanner,
        },
        isSubpage,
        showCompanyBanner: () => setIsShowingCompanyBanner(true),
        toggleFollowPage,
        setPageLogo,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};
